import { isAxiosError } from 'axios'
import { push } from 'connected-react-router'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import type { AppAction, AppState } from '@app/state'

import { convertApiPatient } from './helpers'
import * as Requests from './rest'
import * as Patients from './state'

import type { AxiosResponse} from 'axios';

function* handleGetPatientList() {
  yield takeEvery(Patients.ActionType.GetList, getList)
}

function* getList(action: AppAction) {
  if (action.type === Patients.ActionType.GetList) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.getAllPatients()
    const response: AxiosResponse<Requests.PatientListResponse> = yield call(
      request,
      appState.config.apiLocation,
      appState.auth.token,
    )
    const patients = response.data.patients.map(convertApiPatient)
    yield put(Patients.actions.listRetrieved(patients))
  }
}

function* handleGetPatient() {
  yield takeEvery(Patients.ActionType.GetPatient, getPatient)
}

function* getPatient(action: AppAction) {
  if (action.type === Patients.ActionType.GetPatient) {
    const appState: AppState = yield select((a) => a)
    const request = Requests.getPatientById(action.patientId)
    const response: AxiosResponse<Requests.PatientResponse> = yield call(
      request,
      appState.config.apiLocation,
      appState.auth.token,
    )
    const patient = convertApiPatient(response.data.patient)
    yield put(Patients.actions.patientRetrieved(patient))
  }
}

function* handleCreateNewPatient() {
  yield takeEvery(Patients.ActionType.CreatePatient, createPatient)
}

function* createPatient(action: AppAction) {
  if (action.type === Patients.ActionType.CreatePatient) {
    const appState: AppState = yield select((a) => a)
    try {
      yield put(Patients.actions.beginCreatePatient())
      const request = Requests.createPatient(action.patient)
      const response: AxiosResponse<Requests.CreatePatientResponse> =
        yield call(request, appState.config.apiLocation, appState.auth.token)
      if (response.status === 200) {
        yield put(
          Patients.actions.createPatientSucceeded(
            convertApiPatient(response.data.patient),
          ),
        )
        yield put(push(`/clinic/patient/${response.data.patient.id}`))
      } else {
        yield put(
          Patients.actions.createPatientFailed('Error creating patient'),
        )
      }
    } catch (e) {
      if (isAxiosError(e) && e.response) {
        const data = e.response.data

        if (typeof data === 'string' && data.includes('DecodingFailure')) {
          yield put(
            Patients.actions.createPatientFailed(
              'This patient could not be created. Please check your form inputs.',
            ),
          )
        } else {
          yield put(
            Patients.actions.createPatientFailed(
              data ?? 'Error creating patient',
            ),
          )
        }
      } else {
        yield put(Patients.actions.createPatientFailed('Unexpected error'))
      }
    }
  }
}

function* handleUpdatePatient() {
  yield takeEvery(Patients.ActionType.UpdatePatient, updatePatient)
}

function* updatePatient(action: AppAction) {
  if (action.type === Patients.ActionType.UpdatePatient) {
    const appState: AppState = yield select((a) => a)
    try {
      yield put(Patients.actions.updatePatientBegin())
      const request = Requests.updatePatient(action.id, action.patient)
      const response: AxiosResponse<Requests.UpdatePatientResponse> =
        yield call(request, appState.config.apiLocation, appState.auth.token)
      if (response.status === 200) {
        yield put(
          Patients.actions.updatePatientSuccess(
            convertApiPatient(response.data.patient),
          ),
        )
      } else {
        yield put(Patients.actions.updatePatientFailure())
      }
    } catch (e) {
      yield put(Patients.actions.updatePatientFailure())
    }
  }
}

function* handleFindPatientByMRN() {
  yield takeEvery(Patients.ActionType.FindPatientByMRN, findPatientByMRN)
}

function* findPatientByMRN(action: AppAction) {
  if (action.type === Patients.ActionType.FindPatientByMRN) {
    const appState: AppState = yield select((a) => a)
    try {
      yield put(Patients.actions.beginFindPatientByMRN())
      const request = Requests.findPatientByMRN(action.mrn)
      const response: AxiosResponse<Requests.PatientResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      if (response.status === 200) {
        if (response.data.patient) {
          yield put(
            Patients.actions.findPatientByMRNSucceeded(
              convertApiPatient(response.data.patient),
            ),
          )
        } else {
          yield put(Patients.actions.noPatientFound())
        }
      } else {
        yield put(Patients.actions.findPatientByMRNFailed('server error'))
      }
    } catch (e) {
      yield put(Patients.actions.findPatientByMRNFailed(e.toString()))
    }
  }
}

export const sagas = {
  handleCreateNewPatient,
  handleFindPatientByMRN,
  handleGetPatientList,
  handleGetPatient,
  handleUpdatePatient,
}
